import { css } from '@emotion/react';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import t from 'react-translate';

// Schemas
import { ActivityKey, MinimalActivity } from 'redux/schemas/models/activity';

// Selectors
import { getCourseActivities } from 'redux/selectors/course';

// Components
import NvFormDropdown from 'shared/components/inputs/nv-form-dropdown';
import NvRadioButton from 'shared/components/inputs/nv-radio-button';
import { NvDropdownTextItem } from 'shared/components/inputs/nv-dropdown';

// Styles
import { info, gray2, gray4, gray7, gray6 } from 'styles/global_defaults/colors';
import { halfSpacing } from 'styles/global_defaults/scaffolding';
import { textLargeFontSize } from 'styles/global_defaults/fonts';

const styles = css`
  &.live-event-registration-trigger {
    background-color: ${gray7};
    border-radius: 10px;

    .selected-values {
      display: inline-block;
      margin-left: ${halfSpacing}px;
    }

    .dropdowns {
      visibility: hidden;
      display: flex;
      height: 0;

      .live-event {
        flex: 1;
      }

      .live-event {
        .title {
          font-size: ${textLargeFontSize}px;
        }
      }
    }

    .info {
      flex: 1 0 auto;
      text-align: right;
      color: ${gray2};
      vertical-align: middle;
      line-height: 24px;
    }

    &.on {
      background-color: ${info};

      .dropdowns {
        visibility: visible;
        height: auto;
        margin-bottom: ${halfSpacing}px;
      }
    }

    &.disabled {
      background-color: ${gray6};
      display: flex;

      .info {
        color: ${gray4};
        display: inherit;
        justify-content: flex-end;
        align-items: center;
      }

      .dropdowns {
        display: none;
      }
    }
  }
`;

const LiveEventRegistrationTrigger = () => {
  const { setValue, watch } = useFormContext();
  const courseActivities = useSelector((state) => getCourseActivities(state));

  const [event, liveEvent] = watch(['event', 'liveEventRegistrationTriggerEvent']);

  const isEnabled = event === 'live-event-registration';

  // Get all live events - following same pattern as activity.tsx
  const liveEventsList: MinimalActivity[] = courseActivities[ActivityKey.LIVE_SESSION] ?? [];

  // Build live event dropdown options
  const liveEventOptions: NvDropdownTextItem[] = useMemo(() => {
    return liveEventsList.map((liveSession: MinimalActivity) => ({
      id: liveSession.id,
      type: 'text',
      text: liveSession.title,
      value: liveSession.id,
    }));
  }, [liveEventsList]);

  const noLiveEvents = liveEventsList.length === 0;

  // Clear live event when radio button is not selected
  useEffect(() => {
    if (event !== 'live-event-registration') {
      setValue('liveEventRegistrationTriggerEvent', null, { shouldValidate: true, shouldDirty: true });
    }
  }, [event, setValue]);

  return (
    <div css={styles} className={`px-4 py-2 mb-4 live-event-registration-trigger ${isEnabled ? 'on' : ''}`}>
      <div className='inline-block mr-2 checkbox-wrapper'>
        <NvRadioButton
          withForm
          value='live-event-registration'
          name='event'
          label={t.COURSE_COMMUNICATIONS.FILTERS.LIVE_EVENT_REGISTRATION.HAS_REGISTERED_TO_EVENT()}
          labelClassName='text-large-regular pl-6 mr-2'
        />
      </div>
      {noLiveEvents && (
        <div className='info text-small text-gray-2 font-weight-bold'>
          {t.COURSE_COMMUNICATIONS.FILTERS.LIVE_EVENT_SESSION.NO_MULTI_SESSION()}
        </div>
      )}
      <div className='dropdowns ml-6'>
        <NvFormDropdown
          name='liveEventRegistrationTriggerEvent'
          className='live-event'
          items={liveEventOptions}
          title={t.COURSE_COMMUNICATIONS.FILTERS.LIVE_EVENT_SESSION.SELECT_EVENT()}
        />
      </div>
    </div>
  );
};

export default LiveEventRegistrationTrigger;
